import { GridColDef } from "@mui/x-data-grid";
import { NonNullableDate } from "@/utils/date-time-utility";
import { useMemo } from "react";
import CustomDataGridV2 from "@/components/CustomDataGridV2";
import { GlobalTableHeightWrapper } from "@/components/GlobalTableHeightWrapper";
import StatusChip from "@/components/StatusChip";
import Text from "@/components/Text";
import AppointmentActionButton from "@/components/app/provider/scheduling/AppointmentActionButton";
import { applyDefaultColumnSettings, DGT_Pagination } from "@/utils/utils";
import {
  APPOINTMENT_MODE,
  APPOINTMENT_MODE_LABEL,
  GROUP_APPOINTMENT_COLUMN_LABEL,
  GROUP_SESSION_ACTION_LABEL,
} from "../group-appointment-constant";
import { GroupAppointment } from "../group-appointment-types";
import { formatTimeAndDuration } from "../group-appointment-utils";

export type GroupAppointmentTableProps = {
  appointments: GroupAppointment[];
  isLoading?: boolean;
  totalCount: number;
  pagination: DGT_Pagination;
  onPaginationChange: (value: DGT_Pagination) => void;
  onRowClick: (appointment: GroupAppointment) => void;
  searchKey?: string;
};

/**
 * Group appointments for the selected range — one row per scheduled session.
 *
 * @author Rajkumar Rathod
 */
const GroupAppointmentTable = ({
  appointments,
  isLoading,
  totalCount,
  pagination,
  onPaginationChange,
  onRowClick,
  searchKey,
}: GroupAppointmentTableProps) => {
  const columns = useMemo<GridColDef<GroupAppointment>[]>(
    () =>
      applyDefaultColumnSettings<GroupAppointment>([
        {
          field: "startTime",
          headerName: GROUP_APPOINTMENT_COLUMN_LABEL.date,
          flex: 0.7,
          minWidth: 110,
          renderCell: ({ row }) => (
            <Text isTabletext>{NonNullableDate(new Date(row.startTime))}</Text>
          ),
        },
        {
          field: "duration",
          headerName: GROUP_APPOINTMENT_COLUMN_LABEL.timeDuration,
          flex: 0.9,
          minWidth: 150,
          renderCell: ({ row }) => (
            <Text isTabletext>
              {formatTimeAndDuration(row.startTime, row.duration)}
            </Text>
          ),
        },
        {
          field: "mode",
          headerName: GROUP_APPOINTMENT_COLUMN_LABEL.mode,
          flex: 0.7,
          minWidth: 110,
          renderCell: ({ row }) => (
            <StatusChip
              label={APPOINTMENT_MODE_LABEL[row.mode]}
              variant={
                row.mode === APPOINTMENT_MODE.VIRTUAL ? "info" : "pending"
              }
            />
          ),
        },
        {
          field: "groupName",
          headerName: GROUP_APPOINTMENT_COLUMN_LABEL.group,
          flex: 1,
          minWidth: 170,
          renderCell: ({ row }) => <Text isTabletext>{row.groupName}</Text>,
        },
        {
          field: "therapistName",
          headerName: GROUP_APPOINTMENT_COLUMN_LABEL.therapist,
          flex: 0.8,
          minWidth: 130,
          renderCell: ({ row }) => <Text isTabletext>{row.therapistName}</Text>,
        },
        {
          field: "sessionStatus",
          headerName: GROUP_APPOINTMENT_COLUMN_LABEL.action,
          flex: 0.6,
          minWidth: 120,
          sortable: false,
          renderCell: ({ row }) => (
            <AppointmentActionButton
              label={GROUP_SESSION_ACTION_LABEL[row.sessionStatus]}
              onClick={() => onRowClick(row)}
            />
          ),
        },
      ]),
    [onRowClick]
  );

  return (
    <GlobalTableHeightWrapper height="100%" flex={1} minHeight={0}>
      <CustomDataGridV2
        rows={appointments}
        columns={columns}
        getRowId={(row) => row.uuid}
        loading={isLoading}
        rowCount={totalCount}
        paginationModel={pagination}
        onPaginationModelChange={onPaginationChange}
        onRowClick={({ row }) => onRowClick(row)}
        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        searchString={searchKey}
      />
    </GlobalTableHeightWrapper>
  );
};

export default GroupAppointmentTable;
